import React, { useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPercent,
  faChevronDown,
  faChevronRight,
  faList
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { useWebSettings } from '../../../context/WebSettingsContext';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { drop } from 'lodash';
import { formatPercentage } from '../../../i18n/FormatCurrency';

// Define the props interface for the Categories component
interface CategoryProps {
  selectedCategory: string;
  setSelectedCategory: React.Dispatch<React.SetStateAction<string>>;
  categories: CategoryData[];
  showAllModal: boolean;
  setShowAllModal: React.Dispatch<React.SetStateAction<boolean>>;
}

// Define the CategoryData interface
interface CategoryData {
  id: number;
  category_name: string;
  category_icon: string;
  discount_percent: string;
  description: string;
}

const Categories: React.FC<CategoryProps> = ({
  selectedCategory,
  setSelectedCategory,
  categories,
  showAllModal,
  setShowAllModal
}) => {
  const { webData } = useWebSettings();
  const isImage = webData?.include_image === 1;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { t } = useTranslation();

  // Show only first 3 categories instead of 4
  const visibleCategories = categories.slice(0, 3);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  return (
    <div className="w-full h-full">
      {dropdownOpen && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40"
          onClick={() => setDropdownOpen(false)}
        />
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="w-full h-full mt-12 pb-6 border-b border-b-gray-300 dark:border-b-stone-700 relative z-40"
      >
        {/* Dropdown for screens under md */}
        <div className="relative w-full px-4 py-2 md:hidden">
          <button
            onClick={toggleDropdown}
            className="flex items-center justify-between w-full px-4 py-2 bg-white dark:bg-stone-700 rounded-md shadow-lg cursor-pointer transition-colors duration-300 ease-in-out"
          >
            <span className="text-black dark:text-zinc-100 font-bold text-lg">
              {selectedCategory === 'all'
                ? t('All Categories')
                : categories.find(cat => cat.id.toString() === selectedCategory)?.category_name}
            </span>
            <FontAwesomeIcon icon={faChevronDown} className="text-black dark:text-zinc-100" />
          </button>
          {dropdownOpen && (
            <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-stone-700 rounded-md shadow-lg z-50">
              <div className="flex flex-col gap-1.5 md:gap-3.5 w-full px-4 py-2">
                {/* All categories button */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={clsx(
                    'flex items-center gap-2 md:gap-3 px-4 py-2 rounded-[1rem] shadow-lg cursor-pointer flex-shrink-0 transition-colors duration-300 ease-in-out',
                    selectedCategory === 'all'
                      ? 'bg-orange-400 shadow-gray-500/40'
                      : 'bg-white dark:bg-stone-700 dark:shadow-gray-500/20 hover:bg-gray-100 hover:dark:bg-stone-600'
                  )}
                  onClick={() => {
                    setSelectedCategory('all');
                    setDropdownOpen(false);
                  }}
                >
                  {isImage && (
                    <img
                      src="https://static.vecteezy.com/system/resources/previews/019/907/094/original/fire-graphic-clipart-design-free-png.png"
                      alt="all category icon"
                      className="w-7 h-7 md:w-11 md:h-11 bg-stone-300 dark:bg-stone-400 p-0.5 md:p-1 rounded-full"
                    />
                  )}
                  <h3 className="text-black dark:text-zinc-100 font-bold text-lg uppercase">
                    {t('ALL')}
                  </h3>
                </motion.div>
                {/* Individual category buttons */}
                {categories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className={clsx(
                      'flex items-center gap-2 md:gap-3 px-4 py-2 rounded-[1rem] shadow-lg cursor-pointer flex-shrink-0 transition-colors duration-300 ease-in-out relative',
                      selectedCategory === category.id.toString()
                        ? 'bg-orange-400 shadow-gray-500/40'
                        : 'bg-white dark:bg-stone-700 dark:shadow-gray-500/20 hover:bg-gray-100 hover:dark:bg-stone-600'
                    )}
                    onClick={() => {
                      setSelectedCategory(category.id.toString());
                      setDropdownOpen(false);
                    }}
                  >
                    {isImage && (
                      <img
                        src={category.category_icon || 'defaultIconUrl.png'}
                        alt={`${category.category_name} category icon`}
                        className="w-7 h-7 md:w-11 md:h-11 bg-stone-300 dark:bg-stone-500 p-0.5 md:p-1 rounded-full"
                      />
                    )}
                    <h3 className="text-black dark:text-zinc-100 font-bold text-lg uppercase">
                      {category.category_name}
                    </h3>
                    {/* Display discount badge if applicable */}
                    {parseFloat(category.discount_percent) > 0 && (
                      <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                        {formatPercentage({ value: category.discount_percent })}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modified desktop view */}
        <div className="hidden md:block w-full px-4 py-2">
          <div className="flex items-center justify-between gap-3.5">
            {/* All categories button */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-[1rem] shadow-lg cursor-pointer transition-colors duration-300 ease-in-out w-[200px] h-[60px]',
                selectedCategory === 'all'
                  ? 'bg-orange-400 shadow-gray-500/40'
                  : 'bg-white dark:bg-stone-700 dark:shadow-gray-500/20 hover:bg-gray-100 hover:dark:bg-stone-600'
              )}
              onClick={() => setSelectedCategory('all')}
            >
              {isImage && (
                <img
                  src="https://static.vecteezy.com/system/resources/previews/019/907/094/original/fire-graphic-clipart-design-free-png.png"
                  alt="all category icon"
                  className="w-7 h-7 md:w-11 md:h-11 bg-stone-300 dark:bg-stone-400 p-0.5 md:p-1 rounded-full flex-shrink-0"
                />
              )}
              <h3 className="text-black dark:text-zinc-100 font-bold text-lg uppercase truncate">
                {t('ALL')}
              </h3>
            </motion.div>

            {/* Visible categories */}
            {visibleCategories.map((category, index) => (
              <motion.div
                key={category.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 rounded-[1rem] shadow-lg cursor-pointer transition-colors duration-300 ease-in-out relative w-[200px] h-[60px]',
                  selectedCategory === category.id.toString()
                    ? 'bg-orange-400 shadow-gray-500/40'
                    : 'bg-white dark:bg-stone-700 dark:shadow-gray-500/20 hover:bg-gray-100 hover:dark:bg-stone-600'
                )}
                onClick={() => setSelectedCategory(category.id.toString())}
              >
                {isImage && (
                  <img
                    src={category.category_icon || 'defaultIconUrl.png'}
                    alt={`${category.category_name} category icon`}
                    className="w-7 h-7 md:w-11 md:h-11 bg-stone-300 dark:bg-stone-500 p-0.5 md:p-1 rounded-full flex-shrink-0"
                  />
                )}
                <h3 className="text-black dark:text-zinc-100 font-bold text-lg truncate">
                  {category.category_name}
                </h3>
                {parseFloat(category.discount_percent) > 0 && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                    {formatPercentage({ value: category.discount_percent })}
                  </div>
                )}
              </motion.div>
            ))}

            {/* See All button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAllModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-[1rem] shadow-lg cursor-pointer bg-white dark:bg-stone-700 hover:bg-gray-100 hover:dark:bg-stone-600 transition-colors duration-300 ease-in-out w-[200px] h-[60px]"
            >
              <FontAwesomeIcon icon={faList} className="text-black dark:text-zinc-100 text-xl" />
              <span className="text-black dark:text-zinc-100 font-semibold">{t('See All')}</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Categories;
